import type { VariantProps } from "@-/com.npmjs.cva";
import { buttonTextVariants, buttonVariants } from "#button-variants";
import { TextClassContext } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  LongPressable,
  type LongPressableProps,
} from "@-/dev.improve.haniverse.components.ui.long-pressable";

/**
 * The app's button primitive: a plain `Pressable` with variant styling, and deliberately nothing
 * else. Everything here is CSS or prop logic — no Reanimated shared values, no animated styles, no
 * `onLayout` measurement, no mount effects — so a screen that mounts hundreds of these pays only
 * for the pressables themselves. Anything animated belongs in a separate, explicitly-named
 * component rather than in the default every consumer reaches for.
 *
 * An `onLongPress` goes through `LongPressable`, which recognises the hold natively (one Gesture
 * Handler recognizer per such button); a button without one stays a plain `Pressable`.
 */
export type ButtonProps = LongPressableProps &
  VariantProps<typeof buttonVariants> & {
    /**
     * Soft-disabled state: gates `onPress` and dims the button, but does NOT set the underlying
     * `Pressable`'s `disabled` attribute. Use this when the button needs to remain hover/tooltip-
     * interactive (e.g. wrapped in a `Tooltip` that explains *why* it's disabled). Set
     * `disabled` instead when you want the platform's full disabled semantics (no focus, no
     * pointer events).
     */
    isDisabled?: boolean;
    /**
     * In-flight state: darkens the background via the `isLoading` compound variants and suppresses
     * `onPress` so an in-flight action can't be double-fired. Purely declarative — there is no
     * spinner or shimmer. A button that needs an explicit progress indicator should render one in
     * its `children` (see `IconButton`, which swaps its glyph for an `ActivityIndicator`).
     */
    isLoading?: boolean;
  };

export function Button({
  accessibilityRole = "button",
  accessible = true,
  children,
  className,
  isDisabled,
  isLoading,
  onPress,
  size,
  variant,
  ...props
}: ButtonProps) {
  return (
    <TextClassContext value={buttonTextVariants({ size, variant })}>
      <LongPressable
        accessibilityRole={accessibilityRole}
        accessible={accessible}
        className={cn(
          // Soft- and hard-disabled both dim the button and signal non-interactivity on web via
          // `cursor-not-allowed`. `isDisabled` keeps the underlying `Pressable` mounted so
          // tooltip/hover handlers still fire; `disabled` blocks them at the platform level.
          (props.disabled || isDisabled) && "cursor-not-allowed opacity-50",
          "overflow-hidden",
          buttonVariants({ isLoading, size, variant }),
          className,
        )}
        // Soft-disabled (`isDisabled`) and loading both block `onPress` without setting the
        // `Pressable`'s `disabled` attribute, so wrapping tooltips/hover handlers keep firing.
        onPress={isLoading || isDisabled ? undefined : onPress}
        role="button"
        {...props}
      >
        {children}
      </LongPressable>
    </TextClassContext>
  );
}
